import type React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { getEntityStatusPaletteColor } from "./OrderStatusChip";

export interface WorkflowStep {
  key: string;
  label: string;
}

interface StatusWorkflowStepperProps {
  steps: WorkflowStep[];
  activeStepKey: string;
  compact?: boolean;
}

const StatusWorkflowStepper: React.FC<StatusWorkflowStepperProps> = ({
  steps,
  activeStepKey,
  compact = false,
}) => {
  const theme = useTheme();
  const activeIndex = Math.max(
    0,
    steps.findIndex((step) => step.key === activeStepKey),
  );

  return (
    <Box sx={{ mt: compact ? 0.5 : 2.5, width: "100%", overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${steps.length}, minmax(${compact ? 64 : 72}px, 1fr))`,
          alignItems: "flex-start",
          minWidth: steps.length * (compact ? 76 : 88),
          px: 0.5,
        }}
      >
        {steps.map((step, index) => {
          const isComplete = index < activeIndex;
          const isActive = index === activeIndex;
          const isReached = isComplete || isActive;
          const showConnector = index < steps.length - 1;
          const stepColor = getEntityStatusPaletteColor(step.key, theme);

          return (
            <Box
              key={step.key}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                px: 0.5,
              }}
            >
              {showConnector && (
                <Box
                  data-testid={`workflow-connector-${index}`}
                  data-reached={index < activeIndex ? "true" : "false"}
                  sx={{
                    position: "absolute",
                    top: index < activeIndex ? 13 : 14,
                    left: "50%",
                    width: "100%",
                    height: index < activeIndex ? 5 : 3,
                    bgcolor: index < activeIndex ? "grey.500" : "divider",
                    zIndex: 0,
                  }}
                />
              )}
              <Box
                data-testid={`workflow-node-${step.key}`}
                data-reached={isReached ? "true" : "false"}
                data-active={isActive ? "true" : "false"}
                sx={{
                  width: compact ? 24 : 28,
                  height: compact ? 24 : 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isReached ? stepColor : "background.paper",
                  color: isReached ? theme.palette.getContrastText(stepColor) : "text.disabled",
                  border: 2,
                  borderColor: isReached ? stepColor : "divider",
                  zIndex: 1,
                  boxShadow: isActive ? `0 0 0 4px ${stepColor}22` : "none",
                }}
              >
                {isComplete || isActive ? (
                  <CheckIcon sx={{ fontSize: compact ? 14 : 16 }} />
                ) : (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "action.disabled",
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: compact ? 0.5 : 1,
                  fontSize: compact ? "0.68rem" : undefined,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? stepColor : isReached ? "text.primary" : "text.secondary",
                  lineHeight: 1.25,
                  px: isActive ? 1 : 0,
                  py: isActive ? 0.25 : 0,
                  borderRadius: 999,
                  bgcolor: isActive ? `${stepColor}18` : "transparent",
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StatusWorkflowStepper;
